import React, { useCallback, useEffect, useState } from 'react';
import { getMemoCounts } from '../../api/classRosterApi';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import { defaultMemoDay } from '../../utils/memoQuick';
import MemoPasteSheet from './MemoPasteSheet';
import MemoSheet from './MemoSheet';

/** 반 학생 칸. 누르면 메모 시트, '여러 명'이면 골라서 같은 메모를, '붙여넣기'면 여러 줄을 한 번에. */
const ClassMemoGrid = ({ roster, students, memoCounts, onMemoCountsChange, studentPath }) => {
  const [sheet, setSheet] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [day, setDay] = useState(() => defaultMemoDay(roster.school_year));

  useEffect(() => { setDay(defaultMemoDay(roster.school_year)); }, [roster.id, roster.school_year]);

  const bump = useCallback((memos, delta) => {
    onMemoCountsChange((prev) => {
      const next = { ...(prev || {}) };
      memos.forEach((memo) => { next[memo.sid] = Math.max(0, (next[memo.sid] || 0) + delta); });
      return next;
    });
  }, [onMemoCountsChange]);
  const onSaved = useCallback(memos => bump(memos, 1), [bump]);
  const onRemoved = useCallback(memos => bump(memos, -1), [bump]);
  const close = useCallback(() => setSheet(null), []);
  // 붙여넣기는 이미 남긴 줄을 다시 보내면 서버가 같은 문서로 받으므로, 칸의 수는 서버 수로 다시 맞춘다.
  const onPasted = useCallback((memos) => {
    bump(memos, 1);
    getMemoCounts(roster.id).then(onMemoCountsChange).catch(() => {});
  }, [bump, roster.id, onMemoCountsChange]);

  const open = (next) => {
    setSheet(next);
    trackClassRosterEvent('class_memo_sheet_opened', {
      roster_id: roster.id,
      scope: next.type === 'one' ? 'one' : next.type === 'group' ? 'group' : 'class',
      student_count: next.type === 'group' ? selected.length : 1,
    });
  };

  const onTile = (student) => {
    if (!selecting) {
      open({ type: 'one', sid: student.sid });
      return;
    }
    setSelected(prev => (prev.includes(student.sid) ? prev.filter(sid => sid !== student.sid) : [...prev, student.sid]));
  };

  const startSelecting = () => {
    setSheet(null);
    setSelected([]);
    setSelecting(true);
  };

  const stopSelecting = () => {
    setSelecting(false);
    setSelected([]);
  };

  const index = sheet?.type === 'one' ? students.findIndex(item => item.sid === sheet.sid) : -1;
  const current = index >= 0 ? students[index] : null;
  const nextStudent = index >= 0 ? students[index + 1] : null;
  const groupTargets = students.filter(item => selected.includes(item.sid));
  const allSelected = selected.length === students.length && students.length > 0;

  return (
    <section className="class-memo" aria-label="학생 메모">
      <div className="class-memo-bar">
        {selecting ? (
          <>
            <strong>{selected.length ? `${selected.length}명 골랐어요` : '함께한 학생을 골라 주세요'}</strong>
            <button type="button" className="class-memo-bar-btn" onClick={() => setSelected(allSelected ? [] : students.map(item => item.sid))}>
              {allSelected ? '모두 빼기' : '모두 고르기'}
            </button>
            <button type="button" className="class-memo-bar-btn" onClick={stopSelecting}>취소</button>
          </>
        ) : (
          <>
            <strong>학생을 누르면 메모해요</strong>
            <button type="button" className="class-memo-bar-btn" onClick={startSelecting}>여러 명에게 메모</button>
            <button type="button" className="class-memo-bar-btn" onClick={() => open({ type: 'paste' })}>여러 줄 붙여넣기</button>
          </>
        )}
      </div>

      <ul className={`class-memo-grid${selecting ? ' is-selecting' : ''}`} data-testid="class-memo-grid">
        {students.map((item) => {
          const count = memoCounts?.[item.sid] || 0;
          const scoring = item.counts?.scoring || 0;
          const isSelected = selected.includes(item.sid);
          return (
            <li key={item.sid}>
              <button
                type="button"
                className={`class-memo-tile${isSelected ? ' is-selected' : ''}${count ? '' : ' is-empty'}`}
                aria-pressed={selecting ? isSelected : undefined}
                aria-label={`${item.number}번 ${item.name}${count ? ` 메모 ${count}개` : ''}`}
                onClick={() => onTile(item)}
              >
                <span className="class-memo-tile-no">{item.number}</span>
                <span className="class-memo-tile-name">{item.name}</span>
                <span className="class-memo-tile-meta">
                  {[count ? `메모 ${count}` : '', scoring ? `채점 ${scoring}` : ''].filter(Boolean).join(' · ') || '메모 없음'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {selecting && (
        <div className="class-memo-dock">
          <button
            type="button"
            className="classes-btn-primary class-sheet-wide"
            disabled={selected.length === 0}
            onClick={() => open({ type: 'group' })}
          >
            {selected.length ? `${selected.length}명에게 남기기` : '학생을 골라 주세요'}
          </button>
        </div>
      )}

      {current && (
        <MemoSheet
          roster={roster}
          targets={[current]}
          memoCount={memoCounts?.[current.sid] || 0}
          day={day}
          onDayChange={setDay}
          onClose={close}
          onSaved={onSaved}
          onRemoved={onRemoved}
          onNext={nextStudent ? () => setSheet({ type: 'one', sid: nextStudent.sid }) : null}
          studentPath={studentPath}
        />
      )}
      {sheet?.type === 'group' && groupTargets.length > 0 && (
        <MemoSheet
          roster={roster}
          targets={groupTargets}
          day={day}
          onDayChange={setDay}
          onClose={close}
          onDone={() => { close(); stopSelecting(); }}
          onSaved={onSaved}
          onRemoved={onRemoved}
          studentPath={studentPath}
        />
      )}
      {sheet?.type === 'paste' && (
        <MemoPasteSheet
          roster={roster}
          students={students}
          day={day}
          onDayChange={setDay}
          onClose={close}
          onSaved={onPasted}
        />
      )}
    </section>
  );
};

export default ClassMemoGrid;
